import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PrimeImportWizard } from "@/components/prime/PrimeImportWizard";
import { PrimeImportHistory } from "@/components/prime/PrimeImportHistory";
import { GrowthContentEditor } from "@/components/prime/GrowthContentEditor";
import { PrimeResultsTable } from "@/components/prime/PrimeResultsTable";
import { toast } from "sonner";

const TEMPLATE = "Agency Number,UPN,Agency Owner Name,Primary District,Tenure,Achievement Level,% of Possible KPI Points Achieved,Points Achieved,Points Possible,PIF Band,Premium Growth Points Achieved\n" +
  "[AGENCY NUMBER],[UPN],[Owner name],[District],0,Prime,72.50%,7.25,10.00,0-999,1.00\n[AGENCY NUMBER],[UPN],[Owner name],[District],5,★★★★,48.00%,4.80,10.00,\"1,000-1,999\",0.50\n";

export const downloadText = (name, text, type = "text/csv") => {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
};

function Instructions() {
  return (
    <details className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700" data-testid="prime-instructions">
      <summary className="font-semibold cursor-pointer text-slate-900">How to import Prime Scores (Group Admins)</summary>
      <ol className="list-decimal ml-5 mt-2 space-y-1">
        <li>In the Farmers Dashboard Prime Agency Program report, choose the Reporting Date, hover over the agency table, click the three dots, then <b>Export data</b> (CSV or XLSX). A PDF printout can't be imported.</li>
        <li>Choose the target group, upload the file, and pick the worksheet and header row.</li>
        <li>Map each column. Agent Code or Top-Recruit Person ID is required; Name and Email only confirm a match.</li>
        <li>Say how the score is stored: a percentage (72.5 or 72.5%), an Excel fraction (0.725), or achieved/available points.</li>
        <li>Set the reporting month if the file doesn't have one. It can differ from the upload month.</li>
        <li>Review the preview, approve flagged rows, resolve ambiguous rows, then click <b>Apply Updates</b>.</li>
        <li>Download the error report for excluded rows. Use <b>Undo Import</b> in Import History if needed.</li>
      </ol>
      <p className="mt-2 text-xs text-amber-700">Column names were checked against the September 2026 District 88 report (Agency Number, UPN, Agency Owner Name, Tenure, Achievement Level, % of Possible KPI Points Achieved, Points Achieved, Points Possible, PIF Band, KPI points). The exported file itself hasn't been tested yet, so review the preview on your first import.</p>
    </details>
  );
}

export default function PrimeScores() {
  const [meta, setMeta] = useState(null);
  const [org, setOrg] = useState("");
  const [tab, setTab] = useState("results");
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    api.get("/prime/import/groups").then((r) => { setMeta(r.data); if (r.data.groups.length === 1) setOrg(r.data.groups[0].id); }).catch((e) => toast.error(apiError(e)));
  }, []);
  if (!meta) return <p className="text-sm text-slate-400 p-6">Loading…</p>;
  if (!meta.can_import) return <p className="p-6 text-sm text-slate-600" data-testid="prime-no-access">Prime Scores are managed by Group Admins. View As is read-only.</p>;
  const group = meta.groups.find((g) => g.id === org);
  return (
    <div data-testid="prime-scores-page">
      <PageHeader title="Prime Scores" description={`Import Farmers Dashboard Prime results (${meta.rules_year} achievement bands), review history, and edit the Path to PRIME page.`}
        actions={<Button variant="outline" onClick={() => downloadText("prime-scores-template.csv", TEMPLATE)} data-testid="prime-template-download"><Download className="w-4 h-4 mr-1.5" />Example template</Button>} />
      <div className="rounded-xl border-2 border-[#006090]/30 bg-[#006090]/5 p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="prime-target-group">
        <Building2 className="w-5 h-5 text-[#006090]" />
        <span className="text-sm font-semibold text-slate-800">Target group</span>
        <select value={org} onChange={(e) => setOrg(e.target.value)} className="h-9 rounded-md border border-slate-300 px-2 text-sm min-w-[220px]" data-testid="prime-group-select">
          <option value="">Choose a group…</option>{meta.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select>
        {group && <span className="text-lg font-bold text-[#013A5A]" data-testid="prime-group-name">{group.name}</span>}
      </div>
      <Instructions />
      {org && (
        <Tabs value={tab} onValueChange={setTab} className="mt-4">
          <TabsList><TabsTrigger value="results" data-testid="prime-tab-results">Results</TabsTrigger>
            <TabsTrigger value="import" data-testid="prime-tab-import">Import Prime Scores</TabsTrigger>
            <TabsTrigger value="history" data-testid="prime-tab-history">Import history</TabsTrigger>
            <TabsTrigger value="content" data-testid="prime-tab-content">Path to PRIME content</TabsTrigger></TabsList>
          <TabsContent value="results" className="mt-4"><PrimeResultsTable org={org} reloadKey={reloadKey} /></TabsContent>
          <TabsContent value="import" className="mt-4"><PrimeImportWizard key={org} org={group} fields={meta.fields} onApplied={() => { setReloadKey((k) => k + 1); }} /></TabsContent>
          <TabsContent value="history" className="mt-4"><PrimeImportHistory org={org} reloadKey={reloadKey} /></TabsContent>
          <TabsContent value="content" className="mt-4"><GrowthContentEditor key={org} org={org} /></TabsContent>
        </Tabs>)}
    </div>
  );
}
